import {
  Badge,
  Checkbox,
  EmptyState,
  Menu,
  type MenuItem,
  Pagination,
  Skeleton,
  Table,
  type TableColumn,
  UserInfo,
  createExitHold,
  createPagedList,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { formatDateTime } from '@/lib/datetime';
import { A, useNavigate } from '@solidjs/router';
import type { DeviceListItem } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { AssignDeviceModal } from './AssignDeviceModal';
import type { DevicesFilters } from './hooks';
import { useAssignDevice, useDeleteDevice, useDevices } from './hooks';

export const STATUS_TONE = {
  available: 'success',
  assigned: 'info',
  lost: 'warning',
  retired: 'neutral',
} as const;

interface Props {
  filters: () => DevicesFilters;
}

export const DevicesTable = (props: Props) => {
  const query = useDevices(props.filters);
  const { session } = useAuth();
  const toast = useToast();
  const [assigning, setAssigning] = createSignal<DeviceListItem | null>(null);
  // Keep the modal mounted (with its target) through the exit animation after close.
  const assignHeld = createExitHold(assigning);
  const [selected, setSelected] = createSignal<Set<string>>(new Set());

  const { rows, page, totalPages, jumping, goToPage } = createPagedList(query, props.filters);

  const can = (key: string) => session()?.permissions.has(key) ?? false;

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.success('Copied');
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const columns = (): readonly TableColumn<DeviceListItem>[] => [
    {
      key: 'select',
      header: '',
      width: '2.5rem',
      render: (row) => (
        <Checkbox
          aria-label={`Select ${row.name}`}
          checked={selected().has(row.id)}
          onChange={() => toggleSelected(row.id)}
        />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <A
          href={`/devices/${row.id}`}
          class="font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          {row.name}
        </A>
      ),
    },
    { key: 'model', header: 'Model', render: (row) => row.model || '—' },
    {
      key: 'os',
      header: 'OS',
      render: (row) => (row.os === 'ios' ? 'iOS' : row.os === 'android' ? 'Android' : 'Other'),
    },
    {
      key: 'serial',
      header: 'Serial / IMEI',
      render: (row) => {
        const value = row.serial || row.imei;
        if (!value) return '—';
        return (
          <button
            type="button"
            title="Click to copy"
            onClick={() => copy(value)}
            class="max-w-40 truncate align-middle font-mono text-xs hover:text-brand-600 dark:hover:text-brand-400"
          >
            {value}
          </button>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>,
    },
    {
      key: 'assigned',
      header: 'Assigned to',
      render: (row) => <UserInfo user={row.assignedUser} />,
    },
    {
      key: 'lastSeen',
      header: 'Last seen',
      render: (row) => (row.lastSeenAt ? formatDateTime(row.lastSeenAt) : '—'),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <RowMenu
          row={row}
          canAssign={can('devices:assign')}
          canRetire={can('devices:delete')}
          onAssign={() => setAssigning(row)}
        />
      ),
    },
  ];

  return (
    <>
      <Show when={!query.isPending} fallback={<Skeleton class="h-40" />}>
        <Show
          when={query.data}
          fallback={<EmptyState variant="error" title="Failed to load devices" />}
        >
          <Table
            data={rows()}
            columns={columns()}
            rowKey={(r) => r.id}
            onRowClick={(r) => toggleSelected(r.id)}
            loading={jumping()}
            empty={<EmptyState title="No devices yet" body="Register one to get started." />}
          />
          <Pagination
            class="mt-4"
            page={page()}
            totalPages={totalPages()}
            loading={jumping()}
            onPage={goToPage}
          />
        </Show>
      </Show>

      <Show when={assignHeld()}>
        {(target) => (
          <AssignDeviceModal
            open={assigning() != null}
            deviceId={target().id}
            currentAssigneeId={target().assignedUserId}
            onClose={() => setAssigning(null)}
          />
        )}
      </Show>
    </>
  );
};

const RowMenu = (props: {
  row: DeviceListItem;
  canAssign: boolean;
  canRetire: boolean;
  onAssign: () => void;
}) => {
  const navigate = useNavigate();
  const unassign = useAssignDevice(props.row.id);
  const retire = useDeleteDevice(props.row.id);
  const toast = useToast();
  const confirmDialog = useConfirm();

  const items = (): MenuItem[] => {
    const list: MenuItem[] = [];
    if (props.canAssign) {
      list.push({
        label: props.row.assignedUserId ? 'Reassign…' : 'Assign…',
        onSelect: props.onAssign,
      });
      if (props.row.assignedUserId) {
        list.push({
          label: 'Unassign',
          onSelect: async () => {
            if (
              !(await confirmDialog({
                title: `Unassign ${props.row.name}?`,
                confirmLabel: 'Unassign',
              }))
            )
              return;
            await unassign.mutateAsync({ userId: null });
            toast.success('Device unassigned');
          },
        });
      }
    }
    list.push({ label: 'Edit', onSelect: () => navigate(`/devices/${props.row.id}`) });
    if (props.canRetire && props.row.status !== 'retired') {
      list.push({
        label: 'Retire',
        danger: true,
        onSelect: async () => {
          const ok = await confirmDialog({
            title: `Retire ${props.row.name}?`,
            body: 'It will be removed from inventory.',
            confirmLabel: 'Retire',
            danger: true,
          });
          if (!ok) return;
          await retire.mutateAsync(undefined as never);
          toast.success('Device retired');
        },
      });
    }
    return list;
  };

  return <Menu items={items()} />;
};
